/**
 * Admin — contenu d'un objectif (migration 0514) : la règle du type, puis
 * l'épinglage facultatif de packs et de SKILLCARDS précis. Le programme épinglé
 * se choisit dans le formulaire principal (`programme_id`).
 * Dès qu'un contenu est épinglé, il remplace la règle du type.
 */

import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import {
  OBJECTIVE_RULE_HELP,
  OBJECTIVE_RULE_LABELS,
  objectiveRuleForType,
} from '@jeraisonne/db/objectives';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { stripMath } from '@jeraisonne/db/cards';
import { useCards } from '@/lib/cards/api';
import {
  type ContentPack,
  useObjectiveContentCatalog,
  useObjectiveContentMutation,
  useObjectiveTemplateContent,
} from '@/lib/admin/objective-content';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';

const selectCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';

export function ObjectiveContentEditor({
  templateId,
  type,
  contentType,
  requiresPackSelection,
  programmeId,
}: {
  templateId: string;
  type: string;
  contentType: 'exercises' | 'epreuves';
  requiresPackSelection: boolean;
  programmeId: string | null;
}) {
  const catalog = useObjectiveContentCatalog();
  const content = useObjectiveTemplateContent(templateId);
  const cards = useCards();
  const mutate = useObjectiveContentMutation(templateId);
  const [packToAdd, setPackToAdd] = useState('');

  const packIds = content.data?.packIds ?? [];
  const cardIds = content.data?.cardIds ?? [];
  const pinned = !!programmeId || packIds.length > 0 || cardIds.length > 0;
  const rule = pinned
    ? 'pinned'
    : objectiveRuleForType(type, { requiresPackSelection, contentType });

  const packsById = useMemo(
    () => new Map((catalog.data?.packs ?? []).map((p) => [p.id, p])),
    [catalog.data],
  );
  const publishedCards = useMemo(
    () => (cards.data ?? []).filter((c) => c.status === 'published'),
    [cards.data],
  );
  const cardsById = useMemo(() => new Map(publishedCards.map((c) => [c.id, c])), [publishedCards]);
  const cardItems = useMemo(
    () => publishedCards.filter((c) => !cardIds.includes(c.id)).map(toSkillcardPickerItem),
    [publishedCards, cardIds],
  );
  const packGroups = useMemo(() => {
    const groups = new Map<string, { label: string; packs: ContentPack[] }>();
    for (const p of catalog.data?.packs ?? []) {
      if (packIds.includes(p.id)) continue;
      const key = `${p.level}|${p.subject}`;
      const g = groups.get(key) ?? {
        label: `${levelLabel(p.level)} · ${subjectLabel(p.subject)}`,
        packs: [],
      };
      g.packs.push(p);
      groups.set(key, g);
    }
    return [...groups.values()];
  }, [catalog.data, packIds]);

  if (contentType === 'epreuves') {
    return (
      <p className="text-ink-500 font-sans text-[13px]">
        Objectif d’épreuves : il s’appuie sur les sujets d’examen, pas sur les SKILLCARDS.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-primary-50/60 rounded-xl p-3">
        <p className="font-display text-primary-800 text-[13px] font-semibold">
          {OBJECTIVE_RULE_LABELS[rule]}
        </p>
        <p className="text-ink-600 mt-0.5 font-sans text-[12px]">{OBJECTIVE_RULE_HELP[rule]}</p>
      </div>

      {/* Packs épinglés */}
      <div className="space-y-2">
        <p className="font-display text-ink-700 text-[12px] font-semibold">Packs épinglés</p>
        {packIds.length === 0 ? (
          <p className="text-ink-400 font-sans text-[12px]">Aucun.</p>
        ) : (
          <ul className="space-y-1.5">
            {packIds.map((id) => {
              const p = packsById.get(id);
              return (
                <li
                  key={id}
                  className="border-ink-100 flex items-center gap-2 rounded-xl border bg-white px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate font-sans text-[13px]">
                    {p ? `${p.title} — ${levelLabel(p.level)} · ${subjectLabel(p.subject)}` : id}
                  </span>
                  <button
                    type="button"
                    aria-label={`Retirer le pack ${p?.title ?? ''}`}
                    disabled={mutate.isPending}
                    onClick={() => mutate.mutate({ kind: 'pack', id, action: 'remove' })}
                    className="text-ink-400 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            aria-label="Pack à épingler"
            value={packToAdd}
            onChange={(e) => setPackToAdd(e.target.value)}
            className={selectCls}
          >
            <option value="">Choisir un pack…</option>
            {packGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.packs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button
            type="button"
            disabled={!packToAdd || mutate.isPending}
            onClick={() => {
              mutate.mutate({
                kind: 'pack',
                id: packToAdd,
                action: 'add',
                position: packIds.length,
              });
              setPackToAdd('');
            }}
            className="border-ink-200 text-ink-800 min-h-[2.75rem] shrink-0 rounded-xl border bg-white px-4 font-sans text-[14px] font-semibold disabled:opacity-40"
          >
            Épingler
          </button>
        </div>
      </div>

      {/* SKILLCARDS épinglées */}
      <div className="space-y-2">
        <p className="font-display text-ink-700 text-[12px] font-semibold">SKILLCARDS épinglées</p>
        {cardIds.length === 0 ? (
          <p className="text-ink-400 font-sans text-[12px]">Aucune.</p>
        ) : (
          <ul className="space-y-1.5">
            {cardIds.map((id) => {
              const c = cardsById.get(id);
              return (
                <li
                  key={id}
                  className="border-ink-100 flex items-center gap-2 rounded-xl border bg-white px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate font-sans text-[13px]">
                    {c ? <MathText>{c.title}</MathText> : 'SKILLCARD non publiée'}
                  </span>
                  <button
                    type="button"
                    aria-label={`Retirer la SKILLCARD ${c ? stripMath(c.title) : ''}`}
                    disabled={mutate.isPending}
                    onClick={() => mutate.mutate({ kind: 'card', id, action: 'remove' })}
                    className="text-ink-400 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <SkillcardPickerField
          items={cardItems}
          value={null}
          onChange={(id) => {
            if (id) mutate.mutate({ kind: 'card', id, action: 'add', position: cardIds.length });
          }}
          placeholder="Ajouter une SKILLCARD…"
          disabled={mutate.isPending}
        />
      </div>

      {mutate.isError && (
        <p className="text-accent-600 font-sans text-[13px]">{mutate.error?.message}</p>
      )}
    </div>
  );
}
